import * as React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { Skeleton } from "@/components/ui/skeleton";
import { AnalysisProjectPickerPage } from "@/routes/analysis/AnalysisProjectPicker";
import { AnalysisProjectRunsPage } from "@/routes/analysis/AnalysisProjectRuns";
import { AppsLauncherPage } from "@/routes/apps/AppsLauncher";
import { ArtifactDetailPage } from "@/routes/artifacts/ArtifactDetail";
import { BudgetPage } from "@/routes/budget/Budget";
import { DashboardPage } from "@/routes/dashboard/Dashboard";
import { ProjectDetailPage } from "@/routes/projects/ProjectDetail";
import { ProjectListPage } from "@/routes/projects/ProjectList";
import { SubmitPickerPage } from "@/routes/projects/SubmitPicker";
import { SubmitTilePage } from "@/routes/projects/SubmitTile";
import { RequireSession } from "@/routes/RequireSession";
import { RunDetailPage } from "@/routes/runs/RunDetail";
import { RunListPage } from "@/routes/runs/RunList";
import { SettingsPage } from "@/routes/settings/Settings";
import { CognitoSignInPage } from "@/routes/auth/cognito/SignIn";
import { CognitoAuthCallbackPage } from "@/routes/auth/cognito/AuthCallback";

/**
 * The artifacts tab loads on demand (flowstack#1800): its flow-run grouping
 * and user-term labels are not needed on any other page, and bundling them
 * pushed the entry chunk past the budget `tests/e2e/fonts.spec.ts` guards.
 */
const ArtifactListPage = React.lazy(() =>
  import("@/routes/artifacts/ArtifactList").then((m) => ({ default: m.ArtifactListPage }))
);

/**
 * The Flow submit page loads on demand too (fs#1798): the AFFDO card and its
 * adapter are only needed there, and the page was already the heaviest route
 * still in the entry chunk.
 */
const SubmitFlowPage = React.lazy(() =>
  import("@/routes/projects/SubmitFlow").then((m) => ({ default: m.SubmitFlowPage }))
);

/**
 * The run analysis and the cross-run comparison load on demand too
 * (flowstack#1801), for the same reason: their verdicts table, curve table
 * and figure/data export are needed on those two pages only.
 */
const AnalysisRunViewPage = React.lazy(() =>
  import("@/routes/analysis/AnalysisRunView").then((m) => ({ default: m.AnalysisRunViewPage }))
);
// The flow-run analysis page (fs#1861) lives in the same module, so it shares
// that chunk and its `AnalysisTab` boundary rather than adding a third.
const AnalysisFlowRunViewPage = React.lazy(() =>
  import("@/routes/analysis/AnalysisRunView").then((m) => ({
    default: m.AnalysisFlowRunViewPage,
  }))
);
const AnalysisComparePage = React.lazy(() =>
  import("@/routes/analysis/AnalysisCompare").then((m) => ({ default: m.AnalysisComparePage }))
);

function RouteSuspense({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <React.Suspense fallback={<Skeleton className="h-64 w-full" />}>{children}</React.Suspense>
  );
}

export function App() {
  return (
    <Routes>
      {/* flowstack#945: land on the DASHBOARD, not the Apps launcher.
          M9.5 landed on ``/apps`` so the suite was the first thing a
          signed-in user saw; the operator's ask is that the first
          view answer "what is happening with my work right now?"
          instead — a launcher is a list of doors, not a picture of
          your work. Apps did not move; it is the first panel of the
          dashboard and still its own rail entry.
          The redirect target ``/dashboard`` is auth-gated by the
          ``<RequireSession><AppShell /></RequireSession>`` wrapper
          below, so an unauthenticated visit to ``/`` cascades:
          ``/`` → ``/dashboard`` → ``/auth/cognito/sign-in`` (the
          RequireSession's standard bounce). Per Rupert PR-359 r0 #1:
          this is intentional — the redirect itself stays
          unconditional so route-level auth concerns live with the
          route, not with the root. */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      {/* Cognito sign-in entry point + Hosted UI callback. The pilot
          email/password form is gone after #154; users sign in via
          the ATTMOS suite Hosted UI. */}
      <Route path="/auth/cognito/sign-in" element={<CognitoSignInPage />} />
      <Route path="/auth/callback" element={<CognitoAuthCallbackPage />} />
      <Route
        element={
          <RequireSession>
            <AppShell />
          </RequireSession>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/apps" element={<AppsLauncherPage />} />
        <Route path="/projects" element={<ProjectListPage />} />
        <Route path="/projects/:slug" element={<ProjectDetailPage />} />
        <Route path="/projects/:slug/submit" element={<SubmitPickerPage />} />
        <Route path="/projects/:slug/submit/tile" element={<SubmitTilePage />} />
        <Route
          path="/projects/:slug/submit/flow"
          element={
            <React.Suspense fallback={<Skeleton className="h-64 w-full" />}>
              <SubmitFlowPage />
            </React.Suspense>
          }
        />
        <Route path="/runs" element={<RunListPage />} />
        <Route path="/runs/:run_id" element={<RunDetailPage />} />
        {/* VIZ U4.1: project-scoped analysis surface — pick a project, then
            a completed run, then its analysis. Three levels, three routes,
            matching the existing /projects[/:slug[/submit]] nesting style. */}
        <Route path="/analysis" element={<AnalysisProjectPickerPage />} />
        <Route path="/analysis/:slug" element={<AnalysisProjectRunsPage />} />
        {/* VIZ U2.1: the cross-run overlay. Listed before `:run_id` for
            readability only — React Router v6 ranks a static segment above a
            dynamic one regardless of order, so `/analysis/x/compare` cannot
            be swallowed as a run whose id is "compare". */}
        <Route
          path="/analysis/:slug/compare"
          element={
            <RouteSuspense>
              <AnalysisComparePage />
            </RouteSuspense>
          }
        />
        {/* fs#1861: the list is one row per flow run, and this is where a
            flow run's row opens. Four segments, so it cannot collide with
            `:run_id` below. */}
        <Route
          path="/analysis/:slug/flow-runs/:flow_run_id"
          element={
            <RouteSuspense>
              <AnalysisFlowRunViewPage />
            </RouteSuspense>
          }
        />
        <Route
          path="/analysis/:slug/:run_id"
          element={
            <RouteSuspense>
              <AnalysisRunViewPage />
            </RouteSuspense>
          }
        />
        <Route
          path="/artifacts"
          element={
            <RouteSuspense>
              <ArtifactListPage />
            </RouteSuspense>
          }
        />
        {/* VIZ U3.1: the artifact-detail route, and the mount point for the
            molecule viewer. `RunArtifactCard` has linked at an artifact detail
            since B1 and flowstack#534 D-6 named this route as the viewer's
            home; neither existed until now. The content hash is in the URL, so
            a structure is shareable by link. */}
        <Route path="/artifacts/:artifact_id" element={<ArtifactDetailPage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
